/* ==========================================================================
   THE ARCHIVE — filtering, sorting, pagination
   PR Vault. No dependencies.

   Design notes
   ------------
   • Metadata is parsed from each card's data-* attributes ONCE on init, then
     every subsequent filter/sort works on plain JS objects. No repeated DOM
     reads, no layout thrash.
   • Sorting uses CSS `order` rather than moving nodes. Reordering 10,000 grid
     items by DOM insertion is the thing that makes review pages janky; setting
     an integer property does not touch the DOM tree at all.
   • Filtering toggles `hidden`, batched inside one requestAnimationFrame.
   • State is mirrored into the URL so a filtered view is shareable and
     survives a back button, without ever reloading the page.
   • Metaobject-backed sections are paginated on the server. "Load more" pulls
     the next page through Shopify's Section Rendering API and appends it, so
     the browser only ever holds the pages someone actually asked for.
   ========================================================================== */

(function () {
  'use strict';

  var PARAMS = { type: 'type', verified: 'verified', photo: 'photo', sort: 'sort' };

  function init(root) {
    if (root.__archiveReady) return;
    root.__archiveReady = true;

    var grid = root.querySelector('[data-arc-grid]');
    if (!grid) return;

    var pageSize = parseInt(root.getAttribute('data-page-size'), 10) || 12;

    var catButtons = Array.prototype.slice.call(root.querySelectorAll('[data-arc-cat]'));
    var toggles = Array.prototype.slice.call(root.querySelectorAll('[data-arc-toggle]'));
    var sortSelect = root.querySelector('[data-arc-sort]');
    var countEl = root.querySelector('[data-arc-count]');
    var emptyEl = root.querySelector('[data-arc-empty]');
    var moreWrap = root.querySelector('[data-arc-more]');
    var moreBtn = root.querySelector('[data-arc-more-btn]');

    /* ---- Parse every record exactly once ---------------------------- */
    function parse(el, i) {
      var wrap = el.parentElement === grid ? el : el.parentElement;
      return {
        el: el,
        wrap: wrap,
        i: i,
        type: el.getAttribute('data-record-type') || 'product_review',
        verified: el.getAttribute('data-verified') === 'true',
        photo: el.getAttribute('data-photo') === 'true',
        time: Date.parse(el.getAttribute('data-date') || '') || 0,
        rating: parseFloat(el.getAttribute('data-rating')) || 0,
        helpful: parseFloat(el.getAttribute('data-helpful')) || 0
      };
    }

    var records = Array.prototype.map.call(grid.querySelectorAll('[data-arc-record]'), parse);
    if (!records.length) return;

    var paginated = root.hasAttribute('data-arc-paginated');
    var total = parseInt(root.getAttribute('data-arc-total'), 10) || records.length;

    /* ---- State ------------------------------------------------------- */
    var url = new URL(window.location.href);
    var state = {
      type: url.searchParams.get(PARAMS.type) || 'all',
      verified: url.searchParams.get(PARAMS.verified) === '1',
      photo: url.searchParams.get(PARAMS.photo) === '1',
      sort: url.searchParams.get(PARAMS.sort) || 'newest',
      shown: pageSize
    };

    /* ---- Entrance animation ------------------------------------------ */
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var io = null;
    if ('IntersectionObserver' in window && !reduce) {
      io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-in');
            io.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
      );
      grid.classList.add('is-anim');
      records.forEach(function (r) {
        io.observe(r.el);
      });
    } else {
      records.forEach(function (r) {
        r.el.classList.add('is-in');
      });
    }

    /* ---- Core ---------------------------------------------------------- */
    function matches(r) {
      if (state.type !== 'all' && r.type !== state.type) return false;
      if (state.verified && !r.verified) return false;
      if (state.photo && !r.photo) return false;
      return true;
    }

    function comparator(a, b) {
      if (state.sort === 'rating') {
        if (b.rating !== a.rating) return b.rating - a.rating;
        return b.time - a.time;
      }
      if (state.sort === 'helpful') {
        if (b.helpful !== a.helpful) return b.helpful - a.helpful;
        return b.time - a.time;
      }
      if (b.time !== a.time) return b.time - a.time;
      return a.i - b.i;
    }

    var frame = null;

    function render(animateNew) {
      if (frame) cancelAnimationFrame(frame);
      // requestAnimationFrame never fires in a background tab. A page opened in
      // one — a deep link like ?type=pb middle-clicked into a new tab — would
      // otherwise sit unfiltered until it was brought forward. Paint straight
      // through when the document is hidden; batch normally when it isn't.
      if (document.hidden) {
        frame = null;
        paint(animateNew);
        return;
      }
      frame = requestAnimationFrame(function () {
        paint(animateNew);
      });
    }

    function paint(animateNew) {
      var visible = records.filter(matches).sort(comparator);
      var limit = Math.min(state.shown, visible.length);

      // Hide everything that no longer qualifies, in one pass.
      var inView = new Set();
      for (var i = 0; i < limit; i++) inView.add(visible[i]);

      records.forEach(function (r) {
        var show = inView.has(r);
        if (r.wrap.hidden === show) r.wrap.hidden = !show;
      });

      // Order without touching the DOM tree.
      visible.forEach(function (r, idx) {
        r.wrap.style.order = String(idx);
        if (animateNew && idx < limit && !reduce) {
          r.el.style.setProperty('--arc-delay', Math.min(idx, 8) * 45 + 'ms');
        }
      });

      if (countEl) {
        var noun = visible.length === 1 ? 'record' : 'records';
        countEl.textContent =
          paginated && records.length < total
            ? visible.length + ' of ' + total + ' ' + noun + ' loaded'
            : visible.length + ' ' + noun;
      }
      if (emptyEl) emptyEl.hidden = visible.length !== 0;
      if (moreWrap) moreWrap.hidden = visible.length <= limit;

      catButtons.forEach(function (b) {
        b.setAttribute(
          'aria-selected',
          b.getAttribute('data-arc-cat') === state.type ? 'true' : 'false'
        );
      });
      toggles.forEach(function (b) {
        b.setAttribute('aria-pressed', state[b.getAttribute('data-arc-toggle')] ? 'true' : 'false');
      });
      if (sortSelect) sortSelect.value = state.sort;

      frame = null;
    }

    function syncUrl() {
      var u = new URL(window.location.href);
      var set = function (k, v, on) {
        if (on) u.searchParams.set(k, v);
        else u.searchParams.delete(k);
      };
      set(PARAMS.type, state.type, state.type !== 'all');
      set(PARAMS.verified, '1', state.verified);
      set(PARAMS.photo, '1', state.photo);
      set(PARAMS.sort, state.sort, state.sort !== 'newest');
      window.history.replaceState({}, '', u.pathname + u.search + u.hash);
    }

    function change(fn) {
      fn();
      state.shown = pageSize;
      syncUrl();
      render(true);
    }

    /* ---- Events -------------------------------------------------------- */
    catButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        change(function () {
          state.type = btn.getAttribute('data-arc-cat');
        });
      });
    });

    toggles.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var key = btn.getAttribute('data-arc-toggle');
        change(function () {
          state[key] = !state[key];
        });
      });
    });

    if (sortSelect) {
      sortSelect.addEventListener('change', function () {
        change(function () {
          state.sort = sortSelect.value;
        });
      });
    }

    if (moreBtn) {
      moreBtn.addEventListener('click', function () {
        state.shown += pageSize;
        render(true);
        if (io) {
          records.forEach(function (r) {
            if (!r.wrap.hidden && !r.el.classList.contains('is-in')) io.observe(r.el);
          });
        }
      });
    }

    /* ---- Server-paginated "Load more" ---------------------------------- */
    var nextLink = root.querySelector('[data-arc-next]');
    if (nextLink) {
      nextLink.addEventListener('click', function (e) {
        e.preventDefault();
        var href = nextLink.getAttribute('href');
        if (!href || nextLink.getAttribute('aria-busy') === 'true') return;

        nextLink.setAttribute('aria-busy', 'true');
        nextLink.querySelector('span').textContent = 'Loading…';

        var sep = href.indexOf('?') === -1 ? '?' : '&';
        fetch(href + sep + 'section_id=' + encodeURIComponent(root.getAttribute('data-arc-section-id') || ''), {
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        })
          .then(function (r) {
            if (!r.ok) throw new Error(r.status);
            return r.text();
          })
          .then(function (text) {
            var doc = new DOMParser().parseFromString(text, 'text/html');
            var incoming = doc.querySelector('[data-arc-grid]');
            if (!incoming) throw new Error('no grid in response');

            var added = [];
            Array.prototype.forEach.call(incoming.children, function (child) {
              var node = document.importNode(child, true);
              grid.appendChild(node);
              var art = node.matches('[data-arc-record]') ? node : node.querySelector('[data-arc-record]');
              if (art) added.push(parse(art, records.length + added.length));
            });

            records = records.concat(added);
            if (io) added.forEach(function (r) { io.observe(r.el); });

            var nextIncoming = doc.querySelector('[data-arc-next]');
            if (nextIncoming) {
              nextLink.setAttribute('href', nextIncoming.getAttribute('href'));
            } else {
              nextLink.parentElement.hidden = true;
            }

            state.shown += pageSize;
            render(true);
          })
          .catch(function () {
            // Fall back to a normal page load rather than leaving a dead button.
            window.location.href = href;
          })
          .then(function () {
            nextLink.removeAttribute('aria-busy');
            var span = nextLink.querySelector('span');
            if (span) span.textContent = 'Load more records';
          });
      });
    }

    // Keyboard: left/right across the category tablist.
    root.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      var idx = catButtons.indexOf(document.activeElement);
      if (idx === -1) return;
      e.preventDefault();
      var next = (idx + (e.key === 'ArrowRight' ? 1 : -1) + catButtons.length) % catButtons.length;
      catButtons[next].focus();
      catButtons[next].click();
    });

    render(false);
  }

  function boot() {
    document.querySelectorAll('[data-archive-root]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-init when the Theme Editor swaps a section in.
  document.addEventListener('shopify:section:load', function (e) {
    e.target.querySelectorAll('[data-archive-root]').forEach(init);
  });
})();

/* ==========================================================================
   THE ARCHIVE — submission form
   Swaps the category-specific fieldset when the record type changes.
   Hidden fields are disabled as well as hidden, so a HYROX submission never
   arrives carrying an empty "Weight lost" line.
   ========================================================================== */

(function () {
  'use strict';

  function initForm(form) {
    if (form.__archiveFormReady) return;
    form.__archiveFormReady = true;

    var typeSelect = form.querySelector('[data-arc-type]');
    var sets = Array.prototype.slice.call(form.querySelectorAll('[data-arc-for]'));
    if (!typeSelect || !sets.length) return;

    function apply() {
      var value = typeSelect.value;
      sets.forEach(function (set) {
        var on = set.getAttribute('data-arc-for') === value;
        set.hidden = !on;
        Array.prototype.forEach.call(set.querySelectorAll('input, select, textarea'), function (field) {
          field.disabled = !on;
          if (field.hasAttribute('data-arc-required')) field.required = on;
        });
      });
    }

    typeSelect.addEventListener('change', apply);
    apply();
  }

  function bootForms() {
    document.querySelectorAll('[data-arc-form]').forEach(initForm);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootForms);
  } else {
    bootForms();
  }

  document.addEventListener('shopify:section:load', function (e) {
    e.target.querySelectorAll('[data-arc-form]').forEach(initForm);
  });
})();
